import { InjectionToken } from '@angular/core';
import type { Signal } from '@angular/core';
import { ViewFeatureExpressionType } from '@ups/security';

/**
 * This is the basic structure to configure menu.
 * The UI will use an internal one, that will be re-calculated whenever permissions change.
 *
 * The visible and enabled properties:
 *
 *  It's an expression of type: ViewFeatureExpressionType
 *
 *  Expression samples:
 *    true
 *    "security_id"
 *    [true]
 *    [false, "security_id"]
 *    ["security_id_one", "security_id_two"]
 *    { or: ["security_id_one", "security_id_two"]}
 *    { and: [ "sexurity_id_main", { or: ["security_id_one", "security_id_two"]} ]}
 */

/** Used by UI components, contains extended IMenuConfig data (full URL, evaluated visible/enabled properties, etc.) */
export interface IMenuItem {
  url?: string;

  path?: string;
  exact?: boolean;

  index?: number;

  title: string;
  tooltip?: string;
  tagName?: string; // used alongside tagging/categories
  isCategory?: boolean;

  icon?: string;
  iconClass?: string;
  disableRouting?: boolean;

  /** Optional key for dynamic menu badge count (e.g. active requests). Use with IMenuBadgeService. */
  badgeCountKey?: string;

  // visible?: boolean;
  // enabled?: boolean;
  visible?: ViewFeatureExpressionType;
  enabled?: ViewFeatureExpressionType;

  children?: IMenuItem[];

  action?: () => void;
}

export const MENU_SERVICE_TOKEN = new InjectionToken<IMenuService>(
  'MENU_SERVICE_TOKEN'
);

/** Optional service for dynamic badge counts on menu items (keyed by IMenuItem.badgeCountKey). */
export interface IMenuBadgeService {
  /** Returns a signal for the badge count for the given key, or undefined if not supported. */
  getBadgeCountSignal(key: string): Signal<number> | undefined;
}

export const MENU_BADGE_SERVICE_TOKEN =
  new InjectionToken<IMenuBadgeService | null>('MENU_BADGE_SERVICE_TOKEN');

/**
 * Optional platform hook for launching a form from a menu URL. On mobile the
 * app provides an implementation that opens the form as a modal instead of
 * routing to it (routing tears down the tab outlet tree). UI that navigates
 * menu items (e.g. the side drawer) should consult this first and fall back to
 * normal routing when it returns false (non-form URLs / no launcher provided).
 */
export interface IFormUrlLauncher {
  /** Open the form for `url`; returns true if handled, false to route normally. */
  openFormByUrl(url: string, title?: string): boolean;
}

export const FORM_URL_LAUNCHER = new InjectionToken<IFormUrlLauncher | null>(
  'FORM_URL_LAUNCHER'
);

/**
 * Shape-only drawer profile header (WI 34251): name/initials/avatar for the
 * shared side drawer. Deliberately has no notion of Auth0, Entra, or any
 * other session -- apps map their own user shape onto this.
 */
export interface DrawerUserProfile {
  name?: string;
  initials?: string;
  image?: string;
  hideLogout?: boolean;
}

/**
 * Auth-agnostic source for the shared side drawer's profile header
 * (WI 34251). Apps that already populate the legacy `UserState.selectCurrent`
 * NgRx slice (Auth0 apps: Foreman, My UPS, Safety, Tool Tracker) leave this
 * token unprovided -- `ups-sidedrawer` falls back to that lookup unchanged.
 * Apps with a different session (e.g. Entra-only apps with no employee-profile
 * store, like Ryn's Roost) provide this token instead, so the shared drawer
 * never needs a direct Auth0 or NgRx dependency to show who is logged in.
 */
export interface IDrawerUserSource {
  getProfile(): Signal<DrawerUserProfile | null>;
}

export const DRAWER_USER_SOURCE = new InjectionToken<IDrawerUserSource>(
  'DRAWER_USER_SOURCE'
);

export interface IMenuService {
  staticMenuConfig?: Array<IMenuItem>;
  menuItems: Array<IMenuItem>;
  configureMenu(
    configItems?: Array<IMenuItem>,
    maxDepth?: number,
    skipParseInternal?: boolean
  );

  /** @deprecated use: isMenuItemActive */
  hasActiveUrl(menuItem: IMenuItem, checkChildren?: boolean): boolean;

  isMenuItemActive(menuItem: IMenuItem, checkChildren?: boolean): boolean;
  getMenuItemToActiveUrl(
    menuItems?: IMenuItem[],
    checkChildren?: boolean
  ): IMenuItem;
  fetchFromRoutes(): IMenuItem[];
}
